import { useState } from 'react';
import { Close, Refresh, Security, Visibility } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { LoadingState } from './loading-state';
import { useGetEntitlementsQuery, useGetEntitlementsUsageQuery } from '../store/hooks';
import { formatDateTime } from '../utils/formatting';
import type { EntitlementResponse } from '@store/generated/admin';

interface ProviderEntitlementsSectionProps {
  userId: string;
  organizationId: string;
}

interface UsageDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  entitlement: EntitlementResponse | null;
  userId: string;
  organizationId: string;
}

function UsageDetailsDialog({
  open,
  onClose,
  entitlement,
  userId,
  organizationId,
}: UsageDetailsDialogProps) {
  const { data, isLoading } = useGetEntitlementsUsageQuery(
    {
      userId,
      organizationId,
      capabilityId: entitlement?.capability_id ?? '',
      usagePeriod: entitlement?.usage_period ?? 'daily',
    },
    { skip: !open || !entitlement }
  );

  const events = data?.items || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <div>
            <Typography variant="h6">Usage Details</Typography>
            {entitlement && (
              <Typography variant="body2" className="text-gray-500">
                {entitlement.capability_name} — {entitlement.current_usage ?? 0} /{' '}
                {entitlement.quota ?? '∞'} ({entitlement.usage_period})
              </Typography>
            )}
          </div>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <LoadingState message="Loading usage events..." />
        ) : events.length === 0 ? (
          <div className="text-center py-8">
            <Typography variant="body1" className="text-gray-500">
              No usage events found for this period
            </Typography>
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event ID</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(event => (
                <TableRow key={event.event_id}>
                  <TableCell>
                    <span className="font-mono text-xs">{event.event_id.slice(0, 8)}...</span>
                  </TableCell>
                  <TableCell>
                    {event.source_event_type ? (
                      <Chip label={event.source_event_type} size="small" variant="outlined" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDateTime(new Date(event.created_at))}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data && data.total > data.per_page && (
          <Typography variant="body2" className="text-gray-500 mt-4 text-center">
            Showing {events.length} of {data.total} events
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ProviderEntitlementsSection({
  userId,
  organizationId,
}: ProviderEntitlementsSectionProps) {
  const [selectedEntitlement, setSelectedEntitlement] = useState<EntitlementResponse | null>(null);

  const {
    data: entitlementsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetEntitlementsQuery(
    { userId, organizationId },
    { skip: !userId || !organizationId }
  );

  const entitlements = entitlementsData?.entitlements || [];

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <LoadingState message="Loading entitlements..." />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" className="text-red-600">
            Error loading entitlements
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div>
            <Typography variant="h6">Entitlements</Typography>
            <Typography variant="body2" className="text-gray-500">
              Capabilities this user has access to based on their bundle assignments
            </Typography>
          </div>
          <Tooltip title="Refresh entitlements">
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
              <Refresh className={isFetching ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
        </div>

        {entitlements.length === 0 ? (
          <div className="text-center py-8">
            <Security className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <Typography variant="body1" className="text-gray-500">
              No entitlements found
            </Typography>
            <Typography variant="body2" className="text-gray-400">
              This user has no active bundle assignments
            </Typography>
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Capability</TableCell>
                <TableCell>Bundle</TableCell>
                <TableCell>Metering</TableCell>
                <TableCell>Quota</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entitlements.map((entitlement, index) => (
                <TableRow key={`${entitlement.capability_id}-${index}`}>
                  <TableCell>
                    <div className="flex items-center">
                      <Security className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <span className="font-medium">{entitlement.capability_name}</span>
                        {entitlement.capability_description && (
                          <div className="text-xs text-gray-500">
                            {entitlement.capability_description}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{entitlement.bundle_name}</span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entitlement.metering_type}
                      size="small"
                      color={entitlement.metering_type === 'none' ? 'default' : 'primary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {entitlement.quota !== null ? (
                      <div className="text-sm">
                        <span>{entitlement.quota}</span>
                        {entitlement.usage_period && (
                          <span className="text-gray-500"> / {entitlement.usage_period}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">Unlimited</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {entitlement.quota != null ? (
                      <Tooltip
                        title={`${entitlement.current_usage ?? 0} used, ${entitlement.quota_remaining ?? entitlement.quota} remaining`}
                      >
                        <Box sx={{ minWidth: 100 }}>
                          <div className="text-xs text-gray-600 mb-1">
                            {entitlement.current_usage ?? 0} / {entitlement.quota}
                          </div>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(
                              ((entitlement.current_usage ?? 0) / entitlement.quota) * 100,
                              100
                            )}
                            color={
                              (entitlement.current_usage ?? 0) >= entitlement.quota
                                ? 'error'
                                : (entitlement.current_usage ?? 0) >= entitlement.quota * 0.8
                                  ? 'warning'
                                  : 'primary'
                            }
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entitlement.assignment_source}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {entitlement.quota != null && (
                      <Tooltip title="View usage details">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedEntitlement(entitlement)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <UsageDetailsDialog
        open={!!selectedEntitlement}
        onClose={() => setSelectedEntitlement(null)}
        entitlement={selectedEntitlement}
        userId={userId}
        organizationId={organizationId}
      />
    </Card>
  );
}
